/**
 * Country cell picker grouping (R6).
 * In-use values sit above a divider; the rest stay A–Z.
 * Does not infer in-use from a full world `options` list.
 */
import type { SearchableSelectOption } from '../components/ui/SearchableSelect';
import { normaliseToInvestorCountry } from './locationUtils';

const byLabel = (left: SearchableSelectOption, right: SearchableSelectOption): number =>
  left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });

const byName = (left: string, right: string): number =>
  left.localeCompare(right, undefined, { sensitivity: 'base' });

export const collectInUseCountryValues = (
  rawValues: Array<string | null | undefined>
): string[] => {
  const canonical = new Set<string>();
  const legacy = new Set<string>();
  rawValues.forEach((raw) => {
    const effective = typeof raw === 'string' ? raw.trim() : '';
    if (!effective) return;
    const normalised = normaliseToInvestorCountry(effective);
    if (normalised) {
      canonical.add(normalised);
    } else {
      legacy.add(effective);
    }
  });
  return [...Array.from(canonical).sort(byName), ...Array.from(legacy).sort(byName)];
};

export const partitionCountrySelectOptions = (
  options: SearchableSelectOption[],
  priorityValues?: readonly string[] | null
): { priority: SearchableSelectOption[]; rest: SearchableSelectOption[] } => {
  if (!priorityValues || priorityValues.length === 0) {
    return { priority: [], rest: options };
  }

  const prioritySet = new Set(priorityValues.filter(Boolean));
  const seen = new Set<string>();
  const priority: SearchableSelectOption[] = [];
  const rest: SearchableSelectOption[] = [];

  options.forEach((option) => {
    if (seen.has(option.value)) return;
    seen.add(option.value);
    if (prioritySet.has(option.value)) {
      priority.push(option);
    } else {
      rest.push(option);
    }
  });

  priority.sort(byLabel);
  rest.sort(byLabel);
  return { priority, rest };
};
